import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Clock, Lock, X } from 'lucide-react';
import { useBilling } from '../context/BillingContext';
import { PLAN_LABEL } from '../billingCopy';

/**
 * The trial clock, always visible while it is counting down, and the one
 * upgrade prompt every page shares when the API refuses an action.
 */
export default function TrialBanner() {
  const { billing, limit, dismissLimit } = useBilling();
  if (!billing) return null;
  // A demo workspace is read only by design and says so in its own strip, so
  // the trial-clock banner would be a lie ("your trial has ended").
  if (billing.plan_id === 'demo') return null;

  const { status, plan_name: planName, trial_days_left: daysLeft, usage, limits, invoice_request: invoice } = billing;
  const dealCap = limits?.max_deals_total;
  const dealsUsed = usage?.deals_total ?? 0;
  const monthlyCap = limits?.companies_per_month;
  const monthUsed = usage?.deals_this_month ?? 0;

  let tone = null;
  if (status === 'trialing') {
    const urgent = daysLeft <= 3;
    tone = {
      wrap: urgent ? 'bg-rose-50 border-rose-200 text-rose-900' : 'bg-amber-50 border-amber-200 text-amber-900',
      icon: urgent ? AlertTriangle : Clock,
      title: `${planName}: ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`,
      detail: dealCap
        ? `${dealsUsed} of ${dealCap} deals used, every feature unlocked.`
        : 'Every feature is unlocked.',
    };
  } else if (status === 'pending_invoice') {
    tone = {
      wrap: 'bg-amber-50 border-amber-200 text-amber-900',
      icon: Clock,
      title: invoice?.plan_name ? `${invoice.plan_name} invoice requested` : 'Invoice requested',
      detail: 'Nothing is charged until it is paid. Your access continues meanwhile.',
    };
  } else if (billing.read_only) {
    tone = {
      wrap: 'bg-rose-50 border-rose-200 text-rose-900',
      icon: Lock,
      title: status === 'canceled' ? 'This workspace is read only' : 'Your trial has ended',
      detail: 'Everything you created is still here to read and export. Choose a plan to add more.',
    };
  } else if (monthlyCap && monthUsed >= monthlyCap * 0.8) {
    tone = {
      wrap: 'bg-amber-50 border-amber-200 text-amber-900',
      icon: Clock,
      title: `${monthUsed} of ${monthlyCap} companies used this month`,
      detail: `${planName} includes ${monthlyCap} companies a month.`,
    };
  }

  return (
    <>
      {tone && (
        <div className={`mb-4 flex flex-wrap items-center gap-3 rounded-xl border px-4 py-2.5 text-sm ${tone.wrap}`}>
          <tone.icon className="h-4 w-4 shrink-0" />
          <span className="font-semibold">{tone.title}</span>
          <span className="opacity-80">{tone.detail}</span>
          <Link
            to="/billing"
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-navy-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-800"
          >
            {billing.read_only ? 'Choose a plan' : 'See plans'}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      {limit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-brand-100 p-2 text-brand-800">
                  {limit.code === 'workspace_read_only' ? <Lock className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                </span>
                <h3 className="text-base font-bold text-navy-900">
                  {limit.code === 'workspace_read_only' ? 'Read only' : 'Not in your plan'}
                </h3>
              </div>
              <button onClick={dismissLimit} className="rounded p-1 text-slate-400 hover:bg-slate-100" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-600">{limit.message}</p>
            {limit.upgrade_to && (
              <p className="mt-2 text-xs text-slate-500">
                {PLAN_LABEL[limit.upgrade_to] || limit.upgrade_to} includes this.
              </p>
            )}
            <div className="mt-5 flex gap-2">
              <Link
                to="/billing"
                onClick={dismissLimit}
                className="flex-1 rounded-lg bg-navy-900 px-4 py-2 text-center text-sm font-semibold text-white hover:bg-navy-800"
              >
                See plans
              </Link>
              <button
                onClick={dismissLimit}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
